import { ProvisionOneOfKind } from 'components/pages/policy/type'
import { TaskValue } from 'lib/actions/task'
import { ActionOutcome, CertifyOutcome, GrantOutcome, RevokeOutcome } from 'pbts/c1/api/task/v1/task'
import { IntlShape } from 'react-intl'

export const getOutcome = (task: TaskValue): CertifyOutcome | GrantOutcome | RevokeOutcome | ActionOutcome | undefined => {
    const typ = task?.type?.taskType
    let outcome
    switch (typ?.oneofKind) {
        case 'certify':
            outcome = typ.certify.outcome
            break
        case 'grant':
            outcome = typ.grant.outcome
            break
        case 'revoke':
            outcome = typ.revoke.outcome
            break
        case 'action':
            outcome = typ.action.outcome
            break
        default:
            return undefined
    }
    return outcome
}

export const taskHasCancelledOutcome = (task: TaskValue): boolean => {
    const outcome = getOutcome(task)
    return outcome === CertifyOutcome.CANCELLED || outcome === GrantOutcome.CANCELLED || outcome === RevokeOutcome.CANCELLED
}

export const taskHasDeniedOutcome = (task: TaskValue): boolean => {
    const outcome = getOutcome(task)
    return outcome === GrantOutcome.DENIED || outcome === RevokeOutcome.DENIED
}

export const getOutcomeText = (task: TaskValue, intl: IntlShape): string => {
    const outcome = getOutcome(task)
    const taskType = task?.type?.taskType.oneofKind

    const currentStep = task.policy?.current

    switch (outcome) {
        case CertifyOutcome.CERTIFIED:
        case GrantOutcome.GRANTED:
        case RevokeOutcome.REVOKED:
        case ActionOutcome.SUCCESS:
            switch (taskType) {
                case 'certify':
                    return intl.formatMessage({
                        id: 'components_task_outcome_certified',
                        defaultMessage: 'Certified',
                    })
                case 'revoke':
                    return intl.formatMessage({
                        id: 'components_task_outcome_revoked',
                        defaultMessage: 'Confirmed',
                    })
                case 'grant':
                    return intl.formatMessage({
                        id: 'components_task_outcome_granted',
                        defaultMessage: 'Granted',
                    })
                case 'action':
                    return intl.formatMessage({
                        id: 'components_task_outcome_positive_action',
                        defaultMessage: 'Success',
                    })
                default:
                    return intl.formatMessage({
                        id: 'components_task_outcome_positive_generic',
                        defaultMessage: 'Approved',
                    })
            }
        case CertifyOutcome.DECERTIFIED:
        case GrantOutcome.DENIED:
        case RevokeOutcome.DENIED:
        case ActionOutcome.DENIED:
            switch (taskType) {
                case 'certify':
                    return intl.formatMessage({
                        id: 'components_task_outcome_decertified',
                        defaultMessage: 'Denied',
                    })
                case 'revoke':
                    return intl.formatMessage({
                        id: 'components_task_outcome_declined',
                        defaultMessage: 'Declined',
                    })
                case 'grant':
                    return intl.formatMessage({
                        id: 'components_task_outcome_denied',
                        defaultMessage: 'Denied',
                    })
                case 'action':
                    return intl.formatMessage({
                        id: 'components_task_outcome_negative_action',
                        defaultMessage: 'Denied',
                    })
                default:
                    return intl.formatMessage({
                        id: 'components_task_outcome_negative_generic',
                        defaultMessage: 'Denied',
                    })
            }
        case CertifyOutcome.ERROR:
        case GrantOutcome.ERROR:
        case RevokeOutcome.ERROR:
        case ActionOutcome.ERROR:
            if (currentStep?.instance.oneofKind === 'provision') {
                if (currentStep?.instance.provision.provision?.provisionPolicy?.typ.oneofKind === ProvisionOneOfKind.MANUAL) {
                    if (task.type?.taskType.oneofKind === 'grant') {
                        return intl.formatMessage({
                            id: 'components_task_outcome_error_provision',
                            defaultMessage: 'Requires provisioning',
                        })
                    }
                    if (task.type?.taskType.oneofKind === 'revoke') {
                        return intl.formatMessage({
                            id: 'components_task_outcome_error_deprovision',
                            defaultMessage: 'Requires deprovisioning',
                        })
                    }
                }
            }

            return intl.formatMessage({
                id: 'my_completed_outcome_error',
                defaultMessage: 'Error',
            })
        case CertifyOutcome.CANCELLED:
        case GrantOutcome.CANCELLED:
        case RevokeOutcome.CANCELLED:
        case ActionOutcome.CANCELLED:
            return intl.formatMessage({
                id: 'my_completed_outcome_cancelled',
                defaultMessage: 'Canceled',
            })
        case GrantOutcome.WAIT_TIMED_OUT:
        case RevokeOutcome.WAIT_TIMED_OUT:
        case CertifyOutcome.WAIT_TIMED_OUT:
            return intl.formatMessage({
                id: 'my_completed_outcome_timed_out',
                defaultMessage: 'Timed out',
            })
        case CertifyOutcome.UNSPECIFIED:
        case GrantOutcome.UNSPECIFIED:
        case RevokeOutcome.UNSPECIFIED:
        case ActionOutcome.UNSPECIFIED:
        default:
            return intl.formatMessage({
                id: 'my_completed_outcome_pending',
                defaultMessage: 'Pending',
            })
    }
}
